import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type { JobRepository } from "./job_bloc";
import type { JobRecord, JobStatus } from "./types";

const client = generateClient<Schema>();

/** `Job.counters` and `Job.result` are `a.json()`, so they arrive typed as unknown. */
function asJsonString(value: unknown): string | null {
  if (typeof value === "string") return value;
  return value != null ? JSON.stringify(value) : null;
}

function toJobRecord(raw: Schema["Job"]["type"]): JobRecord {
  return {
    id: raw.id,
    type: raw.type,
    status: (raw.status ?? "pending") as JobStatus,
    triggeredBy: raw.triggeredBy,
    total: raw.total ?? null,
    processed: raw.processed ?? 0,
    counters: asJsonString(raw.counters),
    result: asJsonString(raw.result),
    error: raw.error ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
  };
}

/**
 * Builds a {@link JobRepository} bound to one job `type`.
 *
 * Kickoff goes through the generic `enqueueJob` mutation, which validates the
 * type against the backend's enqueueable-job registry — an unregistered type is
 * rejected there rather than here.
 */
export function createJobRepository(jobType: string): JobRepository {
  return {
    async kickoff(input) {
      const result = await client.mutations.enqueueJob({
        type: jobType,
        ...(input ? { input: JSON.stringify(input) } : {}),
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      if (!result.data?.jobId)
        throw new Error("No jobId returned from mutation");
      return result.data.jobId;
    },

    async findRunningJob() {
      const result = await client.models.Job.list({
        filter: {
          type: { eq: jobType },
          or: [{ status: { eq: "running" } }, { status: { eq: "pending" } }],
        },
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      const first = result.data[0];
      return first ? toJobRecord(first) : null;
    },

    async cancelJob(jobId) {
      const current = await client.models.Job.get({ id: jobId });
      if (current.errors?.length) throw new Error(current.errors[0].message);
      const status = current.data?.status;
      if (status !== "running" && status !== "pending") return;
      const result = await client.models.Job.update({
        id: jobId,
        status: "cancelled",
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
    },

    async getJob(jobId) {
      const result = await client.models.Job.get({ id: jobId });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      return result.data ? toJobRecord(result.data) : null;
    },

    subscribeToJob(jobId, onUpdate, onError) {
      const sub = client.models.Job.onUpdate({
        filter: { id: { eq: jobId } },
      }).subscribe({
        next: (data) => onUpdate(toJobRecord(data)),
        error: (err: unknown) => {
          onError(err instanceof Error ? err : new Error(String(err)));
        },
      });

      return () => sub.unsubscribe();
    },
  };
}

/** Repository for the scheduled/admin database-repair reconcile pass. */
export const reconcileJobRepository = createJobRepository("reconcile");

/** Repository for the admin-authored product update broadcast. */
export const productUpdateBroadcastJobRepository = createJobRepository(
  "product-update-broadcast",
);
